import React from 'react';
import { useParams, Link } from 'react-router-dom';
import SEO from '../components/SEO';
import { CORPORATE_CATEGORIES, PERSONAL_CATEGORIES, PRODUCTS } from '../mock/mock';
import { ProductCard } from '../components/ProductGrid';
import ContactForm from '../components/ContactForm';
import { ChevronRight, ArrowLeft } from 'lucide-react';

const findCategory = (type, id) => {
  const list = type === 'corporate' ? CORPORATE_CATEGORIES : PERSONAL_CATEGORIES;
  return list.find(c => c.id === id) || {
    id, title: id.replace(/-/g, ' ').replace(/\b\w/g, c => c.toUpperCase()),
    tagline: 'Curated hampers, thoughtfully wrapped',
    hero: 'https://images.unsplash.com/photo-1668127494486-f27a1d2b88f9',
    subCategories: [],
  };
};

const CategoryDetail = () => {
  const { type, id } = useParams();
  const category = findCategory(type, id);
  
  // Filter products dynamically by categoryId. Fallback to featured items if none map directly.
  const filteredProducts = PRODUCTS.filter(p => p.categoryId === id);
  const displayProducts = filteredProducts.length > 0 ? filteredProducts : PRODUCTS.slice(0, 8);

  const breadcrumbSchema = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: [
      { '@type': 'ListItem', position: 1, name: 'Home', item: 'https://wrapyourwish.com/' },
      {
        '@type': 'ListItem',
        position: 2,
        name: type === 'corporate' ? 'Corporate Gifting' : 'Personalised Gifting',
        item: `https://wrapyourwish.com/${type === 'corporate' ? 'corporate-gifting' : 'personalised-gifting'}`,
      },
      {
        '@type': 'ListItem',
        position: 3,
        name: category.title,
        item: `https://wrapyourwish.com/category/${type}/${id}`,
      },
    ],
  };

  const productSchemas = displayProducts.map((p) => ({
    '@context': 'https://schema.org',
    '@type': 'Product',
    name: p.name,
    image: p.img,
    description: `${p.name} - ${category.title} gift hamper by WrapYourWish.`,
    sku: p.id,
    brand: { '@type': 'Brand', name: 'WrapYourWish' },
    offers: {
      '@type': 'Offer',
      priceCurrency: 'INR',
      price: '1499',
      availability: 'https://schema.org/InStock',
    },
  }));

  return (
    <main>
      <SEO
        title={`${category.title} Hampers & Gifts | WrapYourWish`}
        description={`Explore our curated ${category.title.toLowerCase()} collection. ${category.tagline}. Custom branding, bulk corporate rates & PAN India delivery.`}
        keywords={`${category.title}, corporate gifting India, ${category.title} hampers, custom gifts`}
        canonicalUrl={`https://wrapyourwish.com/category/${type}/${id}`}
        ogImage={category.hero}
        schemas={[breadcrumbSchema, ...productSchemas]}
      />
      {/* Hero */}
      <section className="relative overflow-hidden">
        <div className="absolute inset-0">
          <img
            src={category.hero}
            alt={`${category.title} curated gift hampers - WrapYourWish`}
            width="1200"
            height="500"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/70 via-black/40 to-black/10" />
        </div>
        <div className="relative max-w-7xl mx-auto px-4 py-24 md:py-36">
          <nav className="flex items-center gap-2 text-xs uppercase tracking-widest text-white/80 mb-6">
            <Link to="/" className="hover:text-white">Home</Link>
            <ChevronRight className="w-3 h-3" />
            <Link to={type === 'corporate' ? '/corporate-gifting' : '/personalised-gifting'} className="hover:text-white">
              {type === 'corporate' ? 'Corporate' : 'Personalised'} Gifting
            </Link>
            <ChevronRight className="w-3 h-3" />
            <span className="text-white">{category.title}</span>
          </nav>
          <h1 className="font-serif-display text-5xl md:text-6xl text-white leading-tight max-w-3xl">{category.title}</h1>
          <p className="text-white/90 mt-4 max-w-xl text-lg">{category.tagline}</p>
        </div>
      </section>

      {/* Products */}
      <section id="products" className="py-16 bg-white">
        <div className="max-w-7xl mx-auto px-4">
          <div className="flex items-end justify-between mb-8">
            <div>
              <h2 className="font-serif-display text-3xl md:text-4xl text-[#2C2416]">Hampers in this collection</h2>
              <p className="text-[#5B4A3A] mt-2">All hampers can be customised with your branding.</p>
            </div>
            <Link to={type === 'corporate' ? '/corporate-gifting' : '/personalised-gifting'} className="hidden md:inline-flex items-center gap-2 text-xs uppercase tracking-widest text-[#7A5C4C] hover:text-[#C58B76]">
              <ArrowLeft className="w-4 h-4" /> Back to all
            </Link>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 md:gap-6">
            {displayProducts.map(p => <ProductCard key={p.id} product={p} />)}
          </div>
        </div>
      </section>

      <ContactForm />
    </main>
  );
};

export default CategoryDetail;
